/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useMemo, useState } from "react";

// react-router components
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
// @mui material components
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import { ThemeProvider } from "@mui/material/styles";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Configurator from "examples/Configurator";
import Sidenav from "examples/Sidenav";

// Material Dashboard 2 React themes
import theme from "assets/theme";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";

// Theme with accent from settings (sidenav color → primary, borders)
import getThemeWithAccent from "assets/theme/themeWithAccent";

// RTL plugins
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import { getRoutesByRole } from "routes";

// Material Dashboard 2 React contexts
import {
  setMiniSidenav,
  setOpenConfigurator,
  useMaterialUIController,
} from "context";

// Images
import brandDark from "assets/images/logo-ct-dark.png";
import brandWhite from "assets/images/logo-ct.png";
import SignIn from "layouts/authentication/sign-in";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import {
  GetDashboardReportCall,
  GetMapReportCall,
} from "./redux/api/report.api";
import { persistor, store } from "./redux/store";
import PresentationMobileViewPage from "./prof/presentationTitles/PresentationMobileViewPage";
import i18n from "./i18n";
import { startImpersonation, stopImpersonation } from "./redux/slices/impersonationSlice";
import { setAuth } from "./redux/slices/authSlice";
import ImpersonateBanner from "./components/ImpersonateBanner";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const [, forceUpdate] = useState({});
  const { pathname } = useLocation();

  // Re-render when impersonation changes (we're outside Provider so subscribe manually)
  useEffect(() => {
    let prevActive = store.getState().impersonation?.active;
    return store.subscribe(() => {
      const active = store.getState().impersonation?.active;
      if (active !== prevActive) {
        prevActive = active;
        forceUpdate({});
      }
    });
  }, []);

  // Sync Redux language to i18n (only en/es have locale files for now)
  useEffect(() => {
    const syncLang = () => {
      const lang = store.getState().general?.lang || "en";
      i18n.changeLanguage(lang === "es" ? "es" : "en");
    };
    syncLang();
    return store.subscribe(syncLang);
  }, []);

  const auth = store.getState().auth || {};
  const impersonation = store.getState().impersonation || {};
  const isAuthenticated = auth?.access_token;
  const role = auth?.role;
  const isImpersonating = impersonation?.active && role === "admin";
  const university = isImpersonating ? impersonation?.university : auth?.universityInfo;
  const effectiveRole = isImpersonating ? "university_admin" : role;
  const routes = getRoutesByRole(effectiveRole);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    // Only admin / university_admin should preload the global dashboard stats
    if (role === "admin" || role === "university_admin") {
      GetDashboardReportCall();
      GetMapReportCall();
    }
    document.body.setAttribute("dir", direction);
  }, [direction, role]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return [
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key}
          />,
        ];
      }

      return [];
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      sx={{
        cursor: "pointer",
        bgcolor: darkMode ? "rgba(255,255,255,0.08)" : "background.paper",
        color: darkMode ? "#ffffff" : "text.primary",
        border: "1px solid",
        borderColor: darkMode ? "rgba(255,255,255,0.14)" : "divider",
        boxShadow: darkMode ? "0 10px 28px rgba(0,0,0,0.45)" : undefined,
      }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  const hideSidenavBasePaths = [
    "/presentationMakerPage",
    "/presentationMobileView",
  ];

  const activeTheme = useMemo(() => {
    const baseTheme = darkMode ? themeDark : theme;
    return getThemeWithAccent(baseTheme, sidenavColor);
  }, [darkMode, sidenavColor]);

  return (
    <Provider store={store}>
      <ThemeProvider theme={activeTheme}>
        <CssBaseline />
        {layout === "dashboard" && !hideSidenavBasePaths.some((base) => pathname.startsWith(base)) && (
          <>
            <Sidenav
              color={sidenavColor}
              university={university}
              brand={
                (transparentSidenav && !darkMode) || whiteSidenav
                  ? brandDark
                  : brandWhite
              }
              brandName=""
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        {isAuthenticated && isImpersonating && <ImpersonateBanner />}
        <PersistGate loading={null} persistor={persistor}>
          <Routes>
            {/* Public route for mobile presentation viewer (no auth required) */}
            <Route
              path="/presentationMobileView/:id"
              element={<PresentationMobileViewPage />}
            />

            {isAuthenticated ? (
              <>
                {getRoutes(routes)}
                {role === "student" ? (
                  <Route
                    path="/quiz"
                    element={<Navigate to="/quiz-by-code" replace />}
                  />
                ) : (
                  <Route
                    path="/quiz"
                    element={<Navigate to="/quiz/titles" replace />}
                  />
                )}
                <Route
                  path="/general-quiz"
                  element={<Navigate to="/general-quiz/titles" replace />}
                />
                <Route
                  path="*"
                  element={
                    <Navigate
                      to={role === "student" ? "/histology" : "/dashboard"}
                      replace
                    />
                  }
                />
              </>
            ) : (
              <Route path="*" element={<SignIn />} />
            )}
          </Routes>
        </PersistGate>
      </ThemeProvider>
    </Provider>
  );
}
